import React, { useState } from 'react';

const API_BASE = 'https://api.glowytics.com';

const today = new Date();
const ymd = (d) => d.toISOString().slice(0, 10);
const DEFAULT_FROM = ymd(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000));
const DEFAULT_TO = ymd(today);

export default function AppleSearchAdsTest() {
  // Auth mode: 'credentials' (paste client+key+PEM) or 'token' (paste access_token)
  const [mode, setMode] = useState('credentials');

  // credentials mode fields
  const [clientId, setClientId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [privateKey, setPrivateKey] = useState('');

  // token mode field
  const [accessToken, setAccessToken] = useState('');

  // shared
  const [orgId, setOrgId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [startDate, setStartDate] = useState(DEFAULT_FROM);
  const [endDate, setEndDate] = useState(DEFAULT_TO);

  // result state
  const [loading, setLoading] = useState(null);   // action label or null
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const callApi = async (action) => {
    setError('');
    setResult(null);
    setLoading(action);

    const body = { action, orgId: orgId.trim() || null };
    if (mode === 'credentials') {
      body.credentials = {
        clientId: clientId.trim(),
        keyId: keyId.trim(),
        privateKey,
      };
    } else {
      body.accessToken = accessToken.trim();
    }

    if (action === 'campaign_report' || action === 'keyword_report') {
      body.startDate = startDate;
      body.endDate = endDate;
    }
    if (action === 'keyword_report') {
      body.campaignId = campaignId.trim();
    }

    try {
      const res = await fetch(`${API_BASE}/api/apple-search-ads-test/call`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setResult({ httpStatus: res.status, ...data });
      if (!res.ok && !data?.upstreamData) {
        setError(data.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  const actionButton = (label, action, disabled = false) => (
    <button
      key={action}
      onClick={() => callApi(action)}
      disabled={!!loading || disabled}
      style={{
        ...btn,
        opacity: loading || disabled ? 0.5 : 1,
        cursor: loading || disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {loading === action ? '…' : label}
    </button>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ marginTop: 0 }}>Apple Search Ads — Manual Test</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Hiçbir veri DB'ye yazılmaz. Bu sayfa Apple Ads API v5 plumbing'ini doğrulamak için saf passthrough'tur.
      </p>

      {/* Auth mode toggle */}
      <div style={card}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="radio" checked={mode === 'credentials'} onChange={() => setMode('credentials')} />
            Credential paste (JWT&apos;yi backend mint etsin)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="radio" checked={mode === 'token'} onChange={() => setMode('token')} />
            Hazır access_token paste et
          </label>
        </div>

        {mode === 'credentials' ? (
          <div style={{ display: 'grid', gap: 8 }}>
            <label>Client ID
              <input value={clientId} onChange={(e) => setClientId(e.target.value)} style={inp} />
            </label>
            <label>Key ID
              <input value={keyId} onChange={(e) => setKeyId(e.target.value)} style={inp} />
            </label>
            <label>Private Key (PEM)
              <textarea
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                rows={6}
                placeholder="-----BEGIN EC PRIVATE KEY-----&#10;...&#10;-----END EC PRIVATE KEY-----"
                style={{ ...inp, fontFamily: 'monospace' }}
              />
            </label>
          </div>
        ) : (
          <label>access_token (Bearer)
            <textarea
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              rows={3}
              placeholder="eyJraWQiOi..."
              style={{ ...inp, fontFamily: 'monospace' }}
            />
          </label>
        )}
      </div>

      {/* Shared params */}
      <div style={card}>
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
          <label>Org ID (orgId)
            <input value={orgId} onChange={(e) => setOrgId(e.target.value)} style={inp} placeholder="X-AP-Context için" />
          </label>
          <label>Campaign ID (keyword_report için)
            <input value={campaignId} onChange={(e) => setCampaignId(e.target.value)} style={inp} />
          </label>
          <label>Start date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inp} />
          </label>
          <label>End date
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inp} />
          </label>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {actionButton('1. Mint Token', 'token')}
        {actionButton('2. List ACLs (orgs)', 'acls')}
        {actionButton('3. List Campaigns', 'campaigns')}
        {actionButton('4. Campaign Report (date range)', 'campaign_report')}
        {actionButton('5. Keyword Report (date range)', 'keyword_report')}
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#900', padding: 12, borderRadius: 6, margin: '12px 0' }}>
          <b>Error:</b> {error}
        </div>
      )}

      {result && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>
            Result — HTTP {result.httpStatus} {result.ok === false ? '(upstream failed)' : ''}
          </h3>
          {result.sentUrl && (
            <div style={{ background: '#f4f4f4', padding: 10, borderRadius: 4, marginBottom: 10, fontFamily: 'monospace', fontSize: 12 }}>
              <div><b>{result.sentMethod}</b> {result.sentUrl}</div>
              {result.sentHeaders && <div>headers: {JSON.stringify(result.sentHeaders)}</div>}
              {result.sentBody && <div>body: {JSON.stringify(result.sentBody)}</div>}
              {result.mintInfo && <div>mintInfo: {JSON.stringify(result.mintInfo)}</div>}
            </div>
          )}
          <pre style={{
            background: '#0d1117', color: '#c9d1d9', padding: 12, borderRadius: 6,
            overflow: 'auto', maxHeight: 600, fontSize: 12, lineHeight: 1.4,
          }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}

      <p style={{ color: '#888', fontSize: 12, marginTop: 20 }}>
        Sıralı çalıştır: <b>1</b>'le token al → <b>2</b> ile org id'yi öğren (boşsa orgId alanına yapıştır) →
        <b>3</b> ile campaign id'leri öğren → <b>4</b>/<b>5</b> ile raporları çek.
      </p>
    </div>
  );
}

const card = { background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 };
const inp = { width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', marginTop: 4, fontSize: 14, boxSizing: 'border-box' };
const btn = { background: '#111', color: 'white', border: 0, padding: '10px 14px', borderRadius: 4, fontSize: 14 };
